import { useCallback } from 'react';
import { isEqual } from 'lodash';
import { Field, FieldRenderProps, RenderableProps } from 'react-final-form';

import {
  RetailerNetworkUsersFilter,
  RetailerNetworkUserFragment,
  Retailer_Network_User_Role,
} from '@occc/graphql';
import {
  AutocompleteProps,
  FormInputProps,
  LazyAutocomplete,
  showErrorOnBlurOrDirty,
} from '@occc-app/design-system';

import { useGetRetailerNetworkUsersLazyQuery } from '../../data-access/queries';
import { mapRetailerNetworkUserRoleToText } from '../retailer-connect.utils';

type RetailerNetworkUserSelectContainerProps = Pick<AutocompleteProps, 'onFocus' | 'onBlur'> & {
  value: RetailerNetworkUserFragment[];
  onChange: (values: RetailerNetworkUserFragment[]) => void;
  disabled?: boolean;
  loading?: boolean;
  label: string;
  errorText?: string;
  inputProps?: FormInputProps;
  retailerNetworkUuidEquals: string;
  roleIn?: Retailer_Network_User_Role[];
};

type RetailerNetworkUserSelectFieldProps = {
  name: string;
  label: string;
} & Pick<
  RetailerNetworkUserSelectContainerProps,
  'inputProps' | 'retailerNetworkUuidEquals' | 'roleIn'
>;

const makeFilter = ({
  retailerNetworkUuidEquals,
  nameLike = null,
  roleIn = [Retailer_Network_User_Role.SiteUser, Retailer_Network_User_Role.SiteAdmin],
}: RetailerNetworkUsersFilter): RetailerNetworkUsersFilter => ({
  retailerNetworkUuidEquals,
  nameLike,
  roleIn,
});

const mapQueryResponseToRawArray = (
  response: ReturnType<typeof useGetRetailerNetworkUsersLazyQuery>['response'],
) => response.data || [];

const mapRawResponseItemToAutocompleteOption = (user: RetailerNetworkUserFragment) => ({
  value: user.uuid,
  label: `${user.name} - ${mapRetailerNetworkUserRoleToText[user.role]}`,
  _raw: user,
});

const UserSelect = ({
  retailerNetworkUuidEquals,
  roleIn,
  ...restProps
}: RetailerNetworkUserSelectContainerProps) => (
  <LazyAutocomplete<
    RetailerNetworkUserFragment,
    ReturnType<typeof useGetRetailerNetworkUsersLazyQuery>['response']
  >
    {...restProps}
    useLazyQuery={useGetRetailerNetworkUsersLazyQuery}
    makeQueryFilter={(nameLike: string) =>
      makeFilter({
        retailerNetworkUuidEquals,
        nameLike,
        roleIn,
      })
    }
    mapQueryResponseToRawArray={mapQueryResponseToRawArray}
    mapRawResponseItemToAutocompleteOption={mapRawResponseItemToAutocompleteOption}
  />
);

export const RetailerNetworkUserMultipleSelectField = (
  props: RetailerNetworkUserSelectFieldProps,
) => {
  const renderInput = useCallback<
    NonNullable<RenderableProps<FieldRenderProps<RetailerNetworkUserFragment[]>>['render']>
  >(
    ({ input, meta }) => (
      <UserSelect
        {...props}
        value={input.value || []}
        onFocus={input.onFocus}
        onBlur={input.onBlur}
        onChange={input.onChange}
        errorText={showErrorOnBlurOrDirty({ meta }) ? meta.error : undefined}
      />
    ),
    [props],
  );

  return <Field name={props.name} isEqual={isEqual} render={renderInput} />;
};
